<script lang="ts" setup>
  import { computed, type PropType } from 'vue';
  import { useTranslator } from '@othercommerce/foundation';
  import type { Module } from '@storefront/types';

  const props = defineProps({
    search: { type: String, required: false },
    category: { type: Object, required: false },
    module: { type: Object as PropType<Module>, required: true },
    total: { type: Number, required: false },
  });

  const translator = useTranslator();

  const title = computed(() => {
    if (props.search) {
      return translator.trans('SHOP.COMMON.LABEL.SEARCH', { search: props.search });
    } else if (props.category) {
      // title => general category name used for filters/slug etc.
      // titleSeo => SEO friendly category name for showing as H1 in products for given category
      // if there is no SEO friendly name, put title in H1
      return props.category.titleSeo ?? props.category.title;
    } else if (props.module) {
      return props.module.title;
    } else {
      return translator.trans('SHOP.COMMON.LABEL.PRODUCTS');
    }
  });

  const description = computed(() => {
    if (props.category) {
      return props.category.descriptionShort;
    } else if (props.module) {
      return props.module.descriptionShort;
    } else {
      return null;
    }
  });
</script>
<template>
  <header class="products-heading">
    <SfHeading type="h1" class="products-heading__header">
      <span class="products-heading__title" v-html="title" />
      <span v-if="total" class="products-heading__quantity" v-html="`(${total})`" />
    </SfHeading>
    <p v-if="description" class="products-heading__description" v-html="description" />
  </header>
</template>
<style lang="scss">
  :root {
    --sf-products-heading-gap: var(--gap-card);
    --sf-products-heading-color: var(--color-default);
    --sf-products-heading-transform: normal;
    --sf-products-heading-size: 1.75rem;
    --sf-products-heading-line-height: 2.5rem;

    --sf-products-heading-quantity-size: 1.25rem;
    --sf-products-heading-quantity-color: var(--color-secondary);
    --sf-products-heading-quantity-font-weight: var(--font-weight-regular);
  }

  .products-heading {
    display: flex;
    flex-direction: column;
    gap: var(--sf-products-heading-gap);
    color: var(--sf-products-heading-color);
    text-transform: var(--sf-products-heading-transform);

    &__header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: var(--sf-products-heading-size);
      line-height: var(--sf-products-heading-line-height);
    }

    &__quantity {
      font-size: var(--sf-products-heading-quantity-size);
      color: var(--sf-products-heading-quantity-color);
      font-weight: var(--sf-products-heading-quantity-font-weight);
    }
  }
</style>
